'use strict';

class Auth {
  get token () {
    return this._storage.getItem('apiDocs:auth:accessToken');
  }

  set token ( token ) {
    this._storage.setItem('apiDocs:auth:accessToken', token);
  }

  get expires () {
    return parseInt(this._storage.getItem('apiDocs:auth:expiresIn') || '0');
  }

  set expires ( expiresInSeconds ) {
    this._storage.setItem('apiDocs:auth:expiresIn', Date.now() + ( expiresInSeconds * 1000 ));
  }

  /**
   * Creates a new authenticator
   *
   * @param {{tokenUrl: string, clientId: string, authorizationUrl: string, storage: Storage}} options
   */
  constructor ( options = {
    authorizationUrl: '',
    tokenUrl:         '',
    clientId:         'api_docs',
    storage:          window.localStorage,
  } ) {

    /**
     * Holds the authorization server authorization URL
     *
     * @type {string}
     */
    this.authorizationUrl = options.authorizationUrl;

    /**
     * Holds the authorization server token URL
     *
     * @type {string}
     */
    this.tokenUrl = options.tokenUrl;

    /**
     * Holds the OAuth client ID
     *
     * @type {string}
     */
    this.clientId = options.clientId;

    /**
     * Holds the token storage backend to use. Must implement the Storage interface
     *
     * @type {Storage}
     * @private
     */
    this._storage = options.storage;

    if ( this._storage.hasOwnProperty('apiDocs:auth:accessToken') ) {
      if ( this.expires <= Date.now() ) {
        this._storage.removeItem('apiDocs:auth:expiresIn');
        this._storage.removeItem('apiDocs:auth:accessToken');
      }
    }
  }

  /**
   * Checks whether the user has authenticated already
   *
   * @returns {boolean}
   */
  isAuthorized () {
    return this.token && this.expires > Date.now();
  }

  /**
   * Attempts to authorize the user
   *
   * @param   {string}        authorizationCode Authorization code retrieved from the server
   * @param   {string}        redirectUri       Application redirect URI
   * @returns {Promise<void>}
   */
  async authorize ( authorizationCode, redirectUri ) {
    const body = new FormData();
    body.append('grant_type', 'authorization_code');
    body.append('client_id', this.clientId);
    body.append('code', authorizationCode);
    body.append('redirect_uri', redirectUri);

    let response;

    try {
      response = await fetch(this.tokenUrl, {
        method: 'POST',
        body,
      });

      if ( !response.ok ) {
        // noinspection ExceptionCaughtLocallyJS
        throw new Error('Failed to authorize: ' + await response.text());
      }
    } catch ( error ) {
      throw new Error('Authorization failed: The server sent an invalid response. Please try again later.');
    }

    /**
     * @type {{access_token: string, expires_in: number, scopes?: string}}
     */
    const parsedResponse = await response.json();

    this.token   = parsedResponse.access_token;
    this.expires = Date.now() + ( parsedResponse.expires_in * 1000 );
  }
}

export default Auth;
